import React, { useState } from 'react';
import { Button, Modal, Switch, Typography, Space, Divider } from 'antd';
import { SettingOutlined } from '@ant-design/icons';

const { Text, Title, Link } = Typography;

interface CookieConsentBannerProps {
    onAcceptAll: () => Promise<void>;
    onRejectAll: () => Promise<void>;
    onSavePreferences: (analytics: boolean, marketing: boolean) => Promise<void>;
}

/**
 * Cookie consent banner component
 * Shows at the bottom of the screen when cookie consent is required
 */
export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
    onAcceptAll,
    onRejectAll,
    onSavePreferences,
}) => {
    const [showSettings, setShowSettings] = useState(false);
    const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
    const [marketingEnabled, setMarketingEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleAcceptAll = async () => {
        setIsLoading(true);
        try {
            await onAcceptAll();
        } finally {
            setIsLoading(false);
        }
    };

    const handleRejectAll = async () => {
        setIsLoading(true);
        try {
            await onRejectAll();
        } finally {
            setIsLoading(false);
        }
    };

    const handleSavePreferences = async () => {
        setIsLoading(true);
        try {
            await onSavePreferences(analyticsEnabled, marketingEnabled);
            setShowSettings(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* Fixed banner at bottom of screen */}
            <div
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: '#ffffff',
                    borderTop: '1px solid #e5e7eb',
                    boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
                    padding: '16px 24px',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                }}
            >
                <div
                    style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            flexWrap: 'wrap',
                            gap: '16px',
                        }}
                    >
                        <div style={{ flex: 1, minWidth: '300px' }}>
                            <Title level={5} style={{ margin: '0 0 8px 0' }}>
                                We use cookies
                            </Title>
                            <Text type="secondary" style={{ fontSize: '14px' }}>
                                We use cookies to enhance your experience, analyze site usage, and assist in our marketing efforts.
                                You can customize your preferences or accept all cookies.{' '}
                                <Link href="/privacy-policy" target="_blank">
                                    Learn more
                                </Link>
                            </Text>
                        </div>

                        <Space wrap style={{ flexShrink: 0 }}>
                            <Button
                                onClick={() => setShowSettings(true)}
                                icon={<SettingOutlined style={{ marginRight: 4 }} />}
                                disabled={isLoading}
                            >
                                Customize
                            </Button>
                            <Button onClick={handleRejectAll} loading={isLoading}>
                                Reject All
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleAcceptAll}
                                loading={isLoading}
                            >
                                Accept All
                            </Button>
                        </Space>
                    </div>
                </div>
            </div>

            {/* Settings Modal */}
            <Modal
                title="Cookie Preferences"
                open={showSettings}
                onCancel={() => setShowSettings(false)}
                footer={[
                    <Button key="cancel" onClick={() => setShowSettings(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={handleSavePreferences}
                        loading={isLoading}
                    >
                        Save Preferences
                    </Button>,
                ]}
                width={500}
            >
                <div style={{ padding: '8px 0' }}>
                    {/* Essential Cookies */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '16px',
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <Text strong>Essential Cookies</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                Required for the website to function properly. Cannot be disabled.
                            </Text>
                        </div>
                        <Switch checked disabled style={{ marginLeft: '16px' }} />
                    </div>

                    <Divider style={{ margin: '12px 0' }} />

                    {/* Analytics Cookies */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '16px',
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <Text strong>Analytics Cookies</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                Help us understand how visitors interact with our website by collecting
                                and reporting information anonymously.
                            </Text>
                        </div>
                        <Switch
                            checked={analyticsEnabled}
                            onChange={setAnalyticsEnabled}
                            style={{ marginLeft: '16px' }}
                        />
                    </div>

                    <Divider style={{ margin: '12px 0' }} />

                    {/* Marketing Cookies */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <Text strong>Marketing Cookies</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                Used to track visitors across websites to display relevant
                                advertisements.
                            </Text>
                        </div>
                        <Switch
                            checked={marketingEnabled}
                            onChange={setMarketingEnabled}
                            style={{ marginLeft: '16px' }}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default CookieConsentBanner;
